import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useHref } from 'react-router-dom';

import { useLoad } from './Home.hooks';
import { Context } from './Home.context';
import styles from './Home.module.css';

import { Issue, NavbarIconLogo, Article, USER_ROLE } from '../../../constants';
import config from '../../../config';

import {
	isStandAlone,
	Installation,
} from '../../../views/installation/Installation';
import { Loading } from '../../../components/loading/Loading';
import { useTrackingPageView } from '../../../tracking/Tracking.hooks';
import { Icon } from '../../../components/icon/Icon';
import { TemplateMagazine } from '../../../config.type';
import { AUTH_KEY } from '../../../views/home/auth/Authentication.hooks';
import { logOut } from '../../../templates/basic/auth/Auth.hooks';
import { UserRound } from 'lucide-react';
import { ProfileMenu } from '../../../components/menu/Menu';
// import {EyeIndicator} from '../../../views/drafts/Drafts'

const MAX_TOP_STORIES = 24;
const MAX_ISSUES = 10;

const Home = ({ onClickLogOut }: HomeProps) => {
	const location = useLocation();
	const { issues, articles, isLoading } = useLoad();
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const [isAuthenticated] = useState(
		config.app.auth.enabled ? !!localStorage.getItem(AUTH_KEY) : true,
	);
	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	const [, setShowInstallation] = useState<boolean>(!isStandAlone());

	useTrackingPageView({
		title: config.build.title,
		location: location.pathname,
	});
	useEffect(() => {
		document.title = config.build.title;
	}, []);
	return (
		<Context.Provider
			value={{
				issues,
				articles,
				isLoading,
				onClickLogOut,
				isAuthenticated,
				isMenuOpen,
				setIsMenuOpen,
				toggleMenu,
			}}
		>
			<Installation
				show={false}
				onCloseClick={() => {
					localStorage.setItem('showed-installation-option', 'true');
					setShowInstallation(false);
				}}
			/>
			<div className={styles['home']}>
				<View />
			</div>
			<ProfileMenu open={isMenuOpen} setOpen={setIsMenuOpen} />
		</Context.Provider>
	);
};

interface HomeProps {
	onClickLogOut?: (url?: string) => void;
}

function View() {
	return (
		<div className={styles['view']}>
			<Topbar />
			<Content />
			<Navbar />
		</div>
	);
}

function Topbar() {
	return (
		<div className={styles['topbar']}>
			<div>
				<EyeIndicator />
			</div>
			<div className={styles['title']}>
				<NavbarIconLogo />
			</div>
			<div>
				<ProfileButton />
			</div>
		</div>
	);
}

function ProfileButton() {
	const { toggleMenu } = useContext(Context);
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}
	return (
		<button
			type="button"
			className="tooltip down"
			data-text={'Profile'}
			onClick={() => {
				toggleMenu();
			}}
		>
			<UserRound color={'#c3c3c3'} size={24} />
		</button>
	);
}

function EyeIndicator() {
	const role = localStorage.getItem(USER_ROLE);
	if (role === 'admin') {
		return (
			<button className={styles['draft-icon']}>
				<Icon name="eye" />
			</button>
		);
	}
	return null;
}

function Content() {
	const { isLoading } = useContext(Context);
	if (isLoading) {
		return (
			<div className={styles['content']}>
				<Loading />
			</div>
		);
	}
	return (
		<div className={styles['content']}>
			<Issues />
			<TopStories />
			<Privacy />
		</div>
	);
}

function Issues() {
	const { issues } = useContext(Context);
	return (
		<div className={styles['issues']}>
			<div className={styles['title']}>
				<h2>Issues</h2>
				<Link to={'/issues'}>
					<h3>All issues</h3>
				</Link>
			</div>

			<div className={styles['']}>
				{issues
					?.reduce(reduceIssues, [])
					?.map((issue: any) => (
						<IssueItem key={issue.id} {...issue} />
					))}
			</div>
		</div>
	);
}

function IssueItem(issue: Issue) {
	const { id, cover, name, slug } = issue;
	const url = `/preview/${slug ? slug : id}`;
	return (
		<Link to={url} id={`story-${id}`} className={styles['issue']}>
			<figure>
				<img src={cover} alt={name} width="300" />
				<figcaption>{name}</figcaption>
			</figure>
		</Link>
	);
}

function TopStories() {
	const { articles } = useContext(Context);
	const template = config.app.template as TemplateMagazine;

	if (!template.config.featureArticles) {
		return null;
	}

	return (
		<div className={styles['top-stories']}>
			<div className={styles['title']}>
				<h2>Top Stories</h2>
			</div>
			<div className={styles['articles-container']}>
				<div className={styles['articles']}>
					{articles
						?.reduce(reduceFeatureArticle, [])
						?.map((article: any, i: number) => (
							<FeatureArticle key={i} {...article} />
						))}
				</div>
			</div>
		</div>
	);
}

function reduceIssues(acc: any, issue: any) {
	if (acc.length >= MAX_ISSUES) {
		return acc;
	}

	acc.push(issue);

	return acc;
}

function reduceFeatureArticle(acc: any, article: any) {
	if (acc.length >= MAX_TOP_STORIES) {
		return acc;
	}

	acc.push(article);

	return acc;
}

function Privacy() {
	const components = [];
	// const { title } = config.build;
	const { legal } = config.app;
	/*components.push(
		<div className="optanon-toggle-display">
			© Copyright {title}, Inc. All material that appears on this app is
			protected by United States copyright law and may not be reproduced,
			distributed, transmitted, cached, or otherwise used, except with
			prior written permission of {title}, Inc. 2024. All Rights Reserved.
		</div>,
	);*/
	if (legal?.tou) {
		components.push(
			<a key="tou" href={legal.tou.url} target="_blank" rel="noreferrer">
				{legal.tou.label}
			</a>,
		);
		components.push('|');
	}
	if (legal?.privacy) {
		components.push(
			<a
				href={legal.privacy.url}
				key="privacy"
				target="_blank"
				rel="noreferrer"
			>
				{legal.privacy.label}
			</a>,
		);
		components.push('|');
	}
	components.push(
		<div
			style={{ margin: '0px 10px' }}
			key="dns"
			className="optanon-toggle-display"
		>
			Do Not Sell
		</div>,
	);
	components.push('|');
	if (legal?.cookie) {
		components.push(
			<a
				href={legal.cookie.url}
				key="cookie"
				target="_blank"
				rel="noreferrer"
			>
				{legal.cookie.label}
			</a>,
		);
	}
	return (
		<footer>
			<div>{components}</div>
		</footer>
	);
}

function FeatureArticle(featureArticle: Article) {
	let { thumbnail } = featureArticle;
	const { IssueID, issueSlug, id, slug, name, featureImage } = featureArticle;
	const url = useMemo(() => {
		const parts = [];
		if (issueSlug) {
			parts.push(`${issueSlug}`);
		} else {
			parts.push(`${IssueID}`);
		}
		if (slug) {
			parts.push(`${slug}`);
		} else {
			parts.push(`${id}`);
		}
		return `/issues/${parts.join('/')}`;
	}, [IssueID, issueSlug, id, slug]);

	const from = window.location.pathname;
	if (!thumbnail) {
		thumbnail = '';
	} else {
		// HACK This is only used to bust the cached for feature images
		thumbnail = thumbnail
			.replace('image.jpg', 'featured.jpg')
			.replace('v1', `v${getRandomArbitrary(0, 9999)}`);
	}
	if (featureImage) {
		thumbnail = featureImage;
	}
	return (
		<Link to={url} state={{ from }} className={styles['article']}>
			<figure>
				<img src={thumbnail} alt="cover" width={150} height={113} />
			</figure>
			<h3>{name['en']}</h3>
		</Link>
	);
}

function getRandomArbitrary(min: number, max: number) {
	return Math.floor(Math.random() * (max - min + 1)) + min;
}

function Navbar() {
	return (
		<nav>
			<ButtonBar />
		</nav>
	);
}

function ButtonBar() {
	const ref = useRef<HTMLDivElement>(null);
	const { isAuthenticated } = useContext(Context);
	useEffect(() => {
		const root = document.documentElement;
		if (!root) {
			return;
		}
		if (!ref) {
			return;
		}
		if (!ref.current) {
			return;
		}

		const current = ref.current;
		if (current.children.length) {
			root.style.setProperty(
				'--bottom-bar-height',
				'calc(var(--navbar-height) + var(--safe-area-inset))',
			);
		} else {
			root.style.setProperty('--bottom-bar-height', '0px');
		}
	}, [ref, isAuthenticated]);

	return (
		<div ref={ref} className={styles['button-bar']}>
			{isAuthenticated && <ButtonLogOut />}
		</div>
	);
}

const ButtonLogOut = () => {
	const basename = useHref('/logout');
	const isAuthenticated = config.app.auth.enabled
		? !!localStorage.getItem(AUTH_KEY)
		: true;
	const { onClickLogOut } = useContext(Context);
	if (!onClickLogOut) {
		return null;
	}

	if (!config.app.auth.enabled) {
		return null;
	}
	if (!isAuthenticated) {
		return null;
	}
	return (
		<button
			type={'button'}
			title="Log out"
			key={'log-out'}
			data-text="Log out"
			onClick={() => {
				onClickLogOut(basename);
				logOut(basename);
			}}
			className={'tooltip down'}
		>
			Log Out
		</button>
	);
};

export default Home;
